import axios from 'axios';
import { header } from './header.encry';

const API_URL = process.env.REACT_APP_PEGASUS_API_URL;

// ========================================
// Authentication APIs
// ========================================

/**
 * Obtenir des tokens Portal valides
 * Gère automatiquement: cache → refresh → login
 */
export const getTokens = async ({ plateformId, forceLogin = false, forceRefresh = false }) => {
  const res = await axios.post(
    `${API_URL}/glovo-portal/tokens`,
    { plateformId, forceLogin, forceRefresh },
    header()
  );
  return res.data;
};

/**
 * Tester la connexion Portal
 */
export const testConnection = async ({ plateformId }) => {
  const res = await axios.post(
    `${API_URL}/glovo-portal/test`,
    { plateformId },
    header()
  );
  return res.data;
};

/**
 * Créer ou mettre à jour une plateforme Portal
 */
export const loginOrCreate = async ({
  plateformId,
  login,
  password,
  name,
  imap_host,
  imap_port,
  imap_user,
  imap_password,
  imap_encryption,
  storeIds
}) => {
  const res = await axios.post(
    `${API_URL}/glovo-portal/login`,
    {
      plateformId,
      login,
      password,
      name,
      imap_host,
      imap_port,
      imap_user,
      imap_password,
      imap_encryption,
      storeIds
    },
    header()
  );
  return res.data;
};

// ========================================
// Orders APIs
// ========================================

/**
 * Synchroniser les commandes depuis Glovo Portal
 * Utilise le nouveau endpoint /glovo-portal/orders/sync avec GraphQL
 */
export const syncOrders = async ({ plateformId, vendorId, timeFrom, timeTo, globalEntityId = 'GV_MA' }) => {
  const res = await axios.post(
    `${API_URL}/glovo-portal/orders/sync`,
    { plateformId, vendorId, timeFrom, timeTo, globalEntityId },
    header()
  );
  return res.data;
};

/**
 * Synchroniser les commandes avec période spécifique (legacy)
 */
export const syncOrdersHistory = async ({ plateformId, startDate, endDate, storeCode }) => {
  const res = await axios.post(
    `${API_URL}/glovo-portal/sync-orders-history`,
    { plateformId, storeCode, startDate, endDate },
    header()
  );
  return res.data;
};

// ========================================
// Products APIs
// ========================================

/**
 * Synchroniser le catalogue produits
 */
export const syncProducts = async ({ plateformId, storeCode, pageSize = 100 }) => {
  const res = await axios.post(
    `${API_URL}/glovo-portal/sync-products`,
    { plateformId, storeCode, pageSize },
    header()
  );
  return res.data;
};

/**
 * Activer/désactiver un produit
 */
export const toggleProduct = async ({ plateformId, sku, active, storeCode }) => {
  const res = await axios.post(
    `${API_URL}/glovo-portal/toggle-product`,
    { plateformId, storeCode, sku, active },
    header()
  );
  return res.data;
};

/**
 * Créer un produit simple
 */
export const createProduct = async ({ plateformId, product, storeCode }) => {
  const res = await axios.post(
    `${API_URL}/glovo-portal/create-product`,
    { plateformId, storeCode, product },
    header()
  );
  return res.data;
};

/**
 * Résoudre un conflit de produit
 */
export const resolveProductConflict = async ({
  plateformId,
  barcode,
  action,
  productData,
  storeCode
}) => {
  const res = await axios.post(
    `${API_URL}/glovo-portal/resolve-product-conflict`,
    { plateformId, storeCode, barcode, action, productData },
    header()
  );
  return res.data;
};

// ========================================
// Categories APIs
// ========================================

/**
 * Synchroniser les catégories
 */
export const syncCategories = async ({ plateformId, storeCode }) => {
  const res = await axios.post(
    `${API_URL}/glovo-portal/sync-categories`,
    { plateformId, storeCode },
    header()
  );
  return res.data;
};

/**
 * Synchroniser les catégories depuis l'API Glovo Portal
 */
export const syncCategoriesPortal = async ({ plateformId, vendorId, globalEntityId = 'GV_MA', storeId = null }) => {
  const res = await axios.post(
    `${API_URL}/glovo-portal/categories/sync`,
    { plateformId, vendorId, globalEntityId, storeId },
    header()
  );
  return res.data;
};

// ========================================
// Reviews APIs
// ========================================

/**
 * Synchroniser les avis clients
 */
export const syncReviews = async ({ plateformId, storeCode, perPage = 50 }) => {
  const res = await axios.post(
    `${API_URL}/glovo-portal/sync-reviews`,
    { plateformId, storeCode, perPage },
    header()
  );
  return res.data;
};

/**
 * Récupérer tous les avis clients avec pagination automatique
 */
export const fetchAllReviews = async ({ 
  plateformId, 
  globalVendorCodes, 
  fromDate, 
  toDate,
  perPage = 50,
  hasText = false
}) => {
  let allReviews = [];
  let cursor = null;
  let pageCount = 0;

  do {
    pageCount++;
    const payload = {
      plateformId,
      perPage,
      hasText,
      fromDate,
      toDate,
      globalVendorCodes,
      cursor
    };

    const res = await axios.post(
      `${API_URL}/glovo-portal/reviews`,
      payload,
      header()
    );

    const data = res.data;
    if (data && data.reviews) {
      allReviews = [...allReviews, ...data.reviews];
      cursor = data.cursor || null;
    } else {
      break;
    }

    // Safety limit
    if (pageCount >= 100) {
      break;
    }
  } while (cursor);

  return {
    reviews: allReviews,
    total: allReviews.length,
    pages: pageCount
  };
};

// ========================================
// Products APIs
// ========================================

/**
 * Get products by category from Glovo Portal
 */
export const getProductsByCategory = async ({ 
  plateformId, 
  vendorId, 
  catalogId, 
  categoryId, 
  globalEntityId = 'GV_MA' 
}) => {
  const res = await axios.post(
    `${API_URL}/glovo-portal/products/category`,
    { plateformId, vendorId, catalogId, categoryId, globalEntityId },
    header()
  );
  return res.data;
};

/**
 * Sync products by category from Glovo Portal
 */
export const syncProductsByCategory = async ({ 
  plateformId, 
  vendorId, 
  categoryId, 
  globalEntityId = 'GV_MA',
  storeCode = null,
  catalogId = null
}) => {
  const res = await axios.post(
    `${API_URL}/glovo-portal/products/category/sync`,
    { plateformId, vendorId, categoryId, globalEntityId, storeCode, catalogId },
    header()
  );
  return res.data;
};

/**
 * Create product in Glovo Portal
 */
export const createProductPortal = async ({ 
  plateformId, 
  vendorId, 
  productData, 
  globalEntityId = 'GV_MA',
  productId = null
}) => {
  const res = await axios.post(
    `${API_URL}/glovo-portal/products/create`,
    { plateformId, vendorId, productData, globalEntityId, productId },
    header()
  );
  return res.data;
};

/**
 * Delete product from Glovo Portal
 */
export const deleteProduct = async ({ 
  plateformId, 
  vendorId, 
  productId, 
  globalEntityId = 'GV_MA',
  product_id = null
}) => {
  const res = await axios.post(
    `${API_URL}/glovo-portal/products/delete`,
    { plateformId, vendorId, productId, globalEntityId, product_id },
    header()
  );
  return res.data;
};

/**
 * Check product creation command status
 */
export const checkCommandStatus = async ({ 
  plateformId, 
  vendorId, 
  commandId, 
  globalEntityId = 'GV_MA',
  productId = null
}) => {
  const res = await axios.post(
    `${API_URL}/glovo-portal/commands/check`,
    { plateformId, vendorId, commandId, globalEntityId, productId },
    header()
  );
  return res.data;
};

// ========================================
// Addons APIs
// ========================================

/**
 * Synchroniser les addons/options depuis Glovo Portal
 */
export const syncAddonsPortal = async ({ 
  plateformId, 
  vendorId, 
  globalEntityId = 'GV_MA',
  storeId = null
}) => {
  const res = await axios.post(
    `${API_URL}/glovo-portal/addons/sync`,
    { plateformId, vendorId, globalEntityId, storeId },
    header()
  );
  return res.data;
};

/**
 * Synchroniser les addons/options (legacy)
 */
export const syncAddons = async ({ plateformId, storeCode }) => {
  const res = await axios.post(
    `${API_URL}/glovo-portal/sync-addons`,
    { plateformId, storeCode },
    header()
  );
  return res.data;
};

/**
 * Mapper les addons aux produits
 */
export const matchAddons = async ({ plateformId, mappings, storeCode }) => {
  const res = await axios.post(
    `${API_URL}/glovo-portal/match-addons`,
    { plateformId, storeCode, mappings },
    header()
  );
  return res.data;
};

// ========================================
// Images APIs
// ========================================

/**
 * Upload image to Glovo Portal CDN
 */
export const uploadImageToGlovo = async ({ 
  plateformId, 
  vendorId, 
  image, 
  globalEntityId = 'GV_MA',
  itemType = 'PRODUCT'
}) => {
  const formData = new FormData();
  formData.append('plateformId', plateformId);
  formData.append('vendorId', vendorId);
  formData.append('globalEntityId', globalEntityId);
  formData.append('itemType', itemType);
  formData.append('image', image);

  const res = await axios.post(
    `${API_URL}/glovo-portal/upload-image`,
    formData,
    {
      ...header(),
      headers: {
        ...header().headers,
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return res.data;
};

// ========================================
// Partner APIs
// ========================================

/**
 * Récupérer le contenu store partenaire
 */
export const getPartnerStoreContent = async ({ plateformId, storeCode }) => {
  const res = await axios.post(
    `${API_URL}/glovo-portal/partner-store-content`,
    { plateformId, storeCode },
    header()
  );
  return res.data;
};

// ========================================
// Legacy/Compatibility APIs
// ========================================

export const fetchGlovoAvailabilities = async ({ plateformId, force = false }) => {
  const res = await axios.post(
    `${API_URL}/global-glovo/availabilities`,
    { plateformId, force },
    header()
  );
  return res.data;
};

export const fetchGlovoStatus = async ({ plateformId, force = false }) => {
  const res = await axios.post(
    `${API_URL}/global-glovo/status`,
    { plateformId, force },
    header()
  );
  return res.data;
};

export const fetchGlovoReviews = async ({
  plateformId,
  force = false,
  reviewBody = {},
}) => {
  const res = await axios.post(
    `${API_URL}/global-glovo/reviews`,
    { plateformId, force, reviewBody },
    header()
  );
  return res.data;
};

/**
 * Sync promotions from Glovo Portal API
 */
export const syncPromotions = async (payload) => {
  const res = await axios.post(
    `${API_URL}/glovo-portal/promotions/sync`,
    payload,
    header()
  );
  return res.data;
};

export default {
  // Authentication
  getTokens,
  testConnection,
  loginOrCreate,
  // Orders
  syncOrders,
  syncOrdersHistory,
  // Products
  syncProducts,
  toggleProduct,
  createProduct,
  resolveProductConflict,
  getProductsByCategory,
  syncProductsByCategory,
  createProductPortal,
  deleteProduct,
  checkCommandStatus,
  // Categories
  syncCategories,
  syncCategoriesPortal,
  // Reviews
  syncReviews,
  fetchAllReviews,
  // Addons
  syncAddons,
  syncAddonsPortal,
  matchAddons,
  // Images
  uploadImageToGlovo,
  // Partner
  getPartnerStoreContent,
  // Promotions
  syncPromotions,
  // Legacy
  fetchGlovoAvailabilities,
  fetchGlovoStatus,
  fetchGlovoReviews,
};
